import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import { cn } from '@/shared/utils/cn';

export interface PdpDesktopProductDetailsSection {
  id: string;
  title: string;
  content: ReactNode;
  defaultExpanded?: boolean;
  alwaysVisibleContent?: ReactNode;
}

interface PdpDesktopProductDetailsProps {
  topRows: ReactNode;
  sections: PdpDesktopProductDetailsSection[];
  onProductDetailsViewed?: () => void;
}

export function PdpDesktopProductDetails({
  topRows,
  sections,
  onProductDetailsViewed,
}: PdpDesktopProductDetailsProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () =>
      new Set(
        sections
          .filter((section) => section.defaultExpanded !== false)
          .map((section) => section.id)
      )
  );

  const viewedRef = useRef(false);
  const { ref: sectionRef, meetsThreshold } = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.2,
    disconnectOnIntersect: true,
  });

  useEffect(() => {
    if (meetsThreshold && !viewedRef.current) {
      viewedRef.current = true;
      onProductDetailsViewed?.();
    }
  }, [meetsThreshold, onProductDetailsViewed]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  return (
    <div ref={sectionRef} className="w-full border border-[#e2e2e2] bg-white">
      <div className="border-b border-[#eee] bg-[#f9f9f9] px-4 py-2">
        <h3 className="text-xs font-bold uppercase tracking-wide text-black">Product Details</h3>
      </div>

      {topRows}

      {sections.map((section) => {
        const isExpanded = expandedSections.has(section.id);

        return (
          <div key={section.id}>
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              aria-expanded={isExpanded}
              className="flex w-full items-center justify-between border-t border-[#eee] bg-[#f9f9f9] px-4 py-2 text-left"
            >
              <span className="text-xs font-bold uppercase tracking-wide text-black">
                {section.title}
              </span>
              <span
                className={cn(
                  'text-xl font-light leading-none text-[#666]',
                  isExpanded && 'pb-0.5'
                )}
                aria-hidden
              >
                {isExpanded ? '−' : '+'}
              </span>
            </button>
            {section.alwaysVisibleContent ? <div>{section.alwaysVisibleContent}</div> : null}
            {isExpanded ? <div>{section.content}</div> : null}
          </div>
        );
      })}
    </div>
  );
}
